/**
 * Mirror permanente dei contatti accoppiati in localStorage (senza TTL).
 * IndexedDB è la fonte primaria; il mirror sopravvive a purge IDB parziali e riavvii lunghi.
 */
import type { ZionContact } from '../storage/pairingStore.ts'

const LS_KEY = 'zion_paired_contacts_v1'

export type PairedContactMirror = Pick<
  ZionContact,
  'contactId' | 'publicKey' | 'nickname' | 'sessionToken' | 'inboundQueueId' | 'customName' | 'pairedAt'
>

function toMirror(contact: ZionContact): PairedContactMirror {
  return {
    contactId: contact.contactId,
    publicKey: contact.publicKey?.trim() ?? '',
    nickname: contact.nickname?.trim() ?? '',
    sessionToken: contact.sessionToken?.trim() ?? '',
    inboundQueueId: contact.inboundQueueId?.trim() ?? '',
    customName: contact.customName,
    pairedAt: contact.pairedAt ?? Date.now(),
  }
}

export function loadPairedContactsMirror(): PairedContactMirror[] {
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((row): row is PairedContactMirror => row && typeof row === 'object')
      .map((row) => ({
        contactId: String(row.contactId ?? '').trim(),
        publicKey: String(row.publicKey ?? '').trim(),
        nickname: String(row.nickname ?? '').trim(),
        sessionToken: String(row.sessionToken ?? '').trim(),
        inboundQueueId: String(row.inboundQueueId ?? '').trim(),
        customName: row.customName ? String(row.customName).trim() : undefined,
        pairedAt: Number(row.pairedAt) || Date.now(),
      }))
      .filter((row) => row.sessionToken && row.publicKey)
  } catch {
    return []
  }
}

function writeMirror(rows: PairedContactMirror[]): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(rows))
  } catch {
    /* quota / private mode */
  }
}

/** Salva/aggiorna un contatto accoppiato nel mirror permanente. */
export function mirrorPairedContact(contact: ZionContact): void {
  const key = contact.publicKey?.trim()
  const sess = contact.sessionToken?.trim()
  if (!key || !sess) return

  const entry = toMirror(contact)
  const all = loadPairedContactsMirror()
  const sessKey = sess.toLowerCase()
  const idx = all.findIndex(
    (c) =>
      c.contactId === entry.contactId ||
      c.sessionToken.trim().toLowerCase() === sessKey,
  )
  if (idx >= 0) all[idx] = entry
  else all.push(entry)
  writeMirror(all)
}

/** Rimuove dal mirror (solo delete esplicito utente). */
export function removePairedContactFromMirror(hint: string): void {
  const h = hint.trim().toLowerCase()
  if (!h) return
  const next = loadPairedContactsMirror().filter(
    (c) =>
      c.contactId.trim().toLowerCase() !== h &&
      c.sessionToken.trim().toLowerCase() !== h &&
      c.nickname.trim().toLowerCase() !== h,
  )
  writeMirror(next)
}

export function clearPairedContactsMirror(): void {
  try {
    localStorage.removeItem(LS_KEY)
  } catch {
    /* ignore */
  }
}
